import { useState } from 'react';
import { Button, Divider, InlineAlert } from '@ai-tutor/ui';

import { XoneraSpinner } from '@/components/core/common/feedback/XoneraSpinner';
import { useAuth } from '@/contexts/AuthContext';
import { classifyAuthError } from '@/lib/auth/auth-error';
import type { AuthMode } from '@/lib/schemas/auth';

/**
 * Google's "G", in its own colours. Lucide carries no brand marks, and Google's
 * sign-in branding guidelines require the official mark rather than a
 * monochrome stand-in - which is also why it keeps its colours in dark mode.
 */
function GoogleMark() {
  return (
    <svg aria-hidden='true' viewBox='0 0 48 48' className='mr-2 h-4 w-4'>
      <path
        fill='#EA4335'
        d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z'
      />
      <path
        fill='#4285F4'
        d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z'
      />
      <path
        fill='#FBBC05'
        d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z'
      />
      <path
        fill='#34A853'
        d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z'
      />
    </svg>
  );
}

/**
 * "Continue with Google" - one button for both sign-in and sign-up, because
 * with OAuth they are the same action: the first visit creates the account.
 * Renders nothing - divider included - while the flag is off, and in reset
 * mode, which is about a password a Google user does not have.
 *
 * Owns its request rather than going through `useAuthSubmit`: success never
 * returns here (the browser is already leaving for Google's consent screen), so
 * the only state is "on its way" or "failed before it left" - offline, rate
 * limited, provider switched off. Pending is cleared only on failure, so the
 * button stays busy until the page unloads instead of flicking back to idle.
 */
export function GoogleSignInButton({
  mode,
  disabled,
}: {
  mode: AuthMode;
  disabled: boolean;
}) {
  const { signInWithProvider } = useAuth();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (mode === 'reset') return null;

  const start = async () => {
    setPending(true);
    setError(null);
    const { error: oauthError } = await signInWithProvider('google');
    if (oauthError) {
      setError(classifyAuthError(oauthError, 'login').message);
      setPending(false);
    }
  };

  return (
    <>
      {error && <InlineAlert message={error} />}
      <Button
        type='button'
        variant='outline'
        size='touch'
        fullWidth
        onClick={start}
        disabled={disabled || pending}
      >
        {pending ? (
          <XoneraSpinner className='mr-2 h-4 w-4' label='' />
        ) : (
          <GoogleMark />
        )}
        Continue with Google
      </Button>
      <Divider label='Or' />
    </>
  );
}
